import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Copy, GitBranch } from 'lucide-react';
import FieldRenderer from './FieldRenderer';

/**
 * One component row on the builder canvas — sortable among its siblings,
 * clickable to select (opens PropertiesPanel), rendered with the SAME
 * FieldRenderer the live public form uses, so what the admin sees while
 * building is exactly what a visitor will see, not an approximation.
 */
export default function CanvasItem({ component, selected, onSelect, onDelete, onDuplicate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: component.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onSelect(component.id)}
      className={`group relative rounded-[4px] border p-4 bg-white cursor-pointer transition-colors ${
        selected ? 'border-[#0176D3] ring-2 ring-[#0176D3]/15' : 'border-[#D8DDE6] hover:border-[#0176D3]/40'
      } ${isDragging ? 'opacity-40' : ''}`}
    >
      <div className="absolute -left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button {...attributes} {...listeners} className="p-1 text-[#706E6B] hover:text-[#0176D3] cursor-grab active:cursor-grabbing" onClick={(e) => e.stopPropagation()}>
          <GripVertical size={16} />
        </button>
      </div>

      {component.condition?.enabled && (
        <div
          className="absolute -left-2 -top-2 p-1 rounded-full bg-[#0176D3] text-white shadow-sm"
          title="This field only shows conditionally"
        >
          <GitBranch size={11} />
        </div>
      )}

      <div className="absolute -right-2 -top-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        <button
          onClick={(e) => { e.stopPropagation(); onDuplicate(component.id); }}
          className="p-1.5 rounded-full bg-white border border-[#D8DDE6] text-[#706E6B] hover:text-[#0176D3] hover:border-[#0176D3] shadow-sm"
          title="Duplicate"
        >
          <Copy size={12} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(component.id); }}
          className="p-1.5 rounded-full bg-white border border-[#D8DDE6] text-[#706E6B] hover:text-red-500 hover:border-red-300 shadow-sm"
          title="Delete"
        >
          <Trash2 size={12} />
        </button>
      </div>

      <div className="pointer-events-none">
        <FieldRenderer component={component} value={undefined} error={null} onChange={() => {}} />
      </div>
    </div>
  );
}
